import { Button, FlyoutButton, Label, Spinner } from '@acpaas-ui/react-components';
import { useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { branchToString, involvedToString } from '../../lib/dossier.utils';
import dossierHeaderPermissionsService from '../../services/permissions/dossier.header.permissions.service';
import FlyoutContentList from '../core/FlyoutContentList';
import LinkButton from '../core/LinkButton';
import Tooltip from '../core/Tooltip';
import CalendarButton from '../forms/CalendarButton';

const DossierHeader = ({
  user,
  name = '',
  isPrivate = false,
  isChangingPrivateMode,
  isChangingDeadline,
  status = '',
  statuses = [],
  deadlineDate,
  dossierNumber = '',
  branch = [],
  theme = [],
  assigned,
  onAssignedOpen,
  onStatusChange,
  onPrivateModeToggle,
  onDeadlineChange
}) => {
  const tooltipId = useRef(uuid()).current;
  const statusesOptions = statuses?.map((st) => ({ value: st.id, label: st.name }));
  const canSetPrivateMode = dossierHeaderPermissionsService.canSetPrivateMode(user);
  const canChangeStatus = dossierHeaderPermissionsService.canChangeStatus(user);
  const canChangeDeadline = dossierHeaderPermissionsService.canChangeDeadline(user);
  const canAssignPeople = dossierHeaderPermissionsService.canAssignPeople(user);

  return (
    <div className="spo-o-dossier-header u-margin-bottom-sm">
      <h4 className="u-margin-bottom-xs">{name}</h4>
      <p className="u-text-bold u-text-light">
        {`${branchToString(branch) ? `${branchToString(branch)} - ` : ''}${dossierNumber}`}
        {theme.length
          ? theme.map((t) => (
              <Label type="primary" className="u-margin-left-xs" key={t.id}>
                {t.name}
              </Label>
            ))
          : ''}
      </p>
      <div className="spo-o-dossier-header__action-bar u-flex">
        <FlyoutButton
          size="tiny"
          type="success"
          iconRight={canChangeStatus ? 'ai-arrow-down-1' : ''}
          aria-label="Status"
          outline
          flyoutSize="small"
          className={`status-button ${canChangeStatus ? '' : 'status-button--disabled'} u-margin-right-sm`}
          label={status}
        >
          {canChangeStatus && <FlyoutContentList items={statusesOptions} onSelect={onStatusChange} />}
        </FlyoutButton>
        <span className="u-margin-right-xs">Volgende deadline:</span>
        <CalendarButton
          value={deadlineDate}
          onChange={onDeadlineChange}
          disabled={!canChangeDeadline}
          isLoading={isChangingDeadline}
        />
        <span className="u-margin-right-xs">Toegekend aan:</span>
        <LinkButton className="u-margin-right-xs" onClick={onAssignedOpen} disabled={!canAssignPeople}>
          {assigned?.users?.length ? assigned?.users?.map(involvedToString)?.join(', ') ?? '' : 'Geen'}
        </LinkButton>
        <span className="private-mode">Privé modus:</span>
        {isChangingPrivateMode ? (
          <div className="private-mode-loading">
            <Spinner />
          </div>
        ) : (
          <span
            data-for={tooltipId}
            data-tip={
              'Als privé modus ingeschakeld is kunnen enkel de personen die betrokken zijn in dit dossier alles raadplegen.'
            }
          >
            <Button
              className={`private-mode-button ${canSetPrivateMode ? 'enabled' : ''}`}
              negative
              transparent
              type={isPrivate ? 'danger' : 'primary'}
              icon={isPrivate ? 'ai-lock-2' : 'ai-lock-unlock-1'}
              onClick={canSetPrivateMode ? onPrivateModeToggle : undefined}
            />
          </span>
        )}
      </div>
      <Tooltip id={tooltipId} size="sm" />
    </div>
  );
};

export default DossierHeader;
